import { useEffect, useState } from 'react'
import { emptyVideoRefusal } from '@/features/editor/lib/emptyVideoBlocks'
import { designOverflowRefusal, type BlockIssue } from '@/features/editor/lib/publishIssues'
import { TooltipProvider } from '@teach/ui'
import type { CourseWithoutBlocks } from '@/types/course'
import { useUpdateCourseMutation } from '../../coursesApi'
import { isLangSlotComplete } from '../../lib/langCompleteness'
import { seedLangFor } from '../studio/seedContentLang'
import { useHasStartedTranslationRun } from '../studio/useBlocksInRun'
import { hasTranslationRow, translationLangOf } from '../../lib/courseTranslation'
import { LANG_LABEL } from '../../lib/contentLangLabels'
import { useIsCourseAuthor } from '../../hooks/useIsCourseAuthor'
import { ValidationErrorsDialog } from '../ValidationErrorsDialog'
import { EmptyVideoGate, type EmptyVideoGateState } from '../publish/EmptyVideoGate'
import { EmptyVideoStatus } from '../publish/EmptyVideoStatus'
import { PublishDialogBody } from '../publish/PublishDialogBody'
import { useCourseAccess } from '../publish/useCourseAccess'
import { PublishIssuesPanel } from './PublishIssuesPanel'
import type { ViolationGo } from '../publish/violationPlace'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/**
 * Доступ к курсу (закрытый / по ссылке / в каталоге) и раздел каталога — своя
 * вкладка «Доступ» сразу после «Основного». Та же начинка, что в окне «Публикация курса»
 * (`PublishDialog`): тот же `useCourseAccess` и тот же `PATCH /visibility`, поэтому
 * оба места всегда показывают одно и то же состояние. Окна здесь нет — только тело.
 */
interface Props {
  course: CourseWithoutBlocks
  /** Черновик формы: заполненность читаем по нему, а не по серверному курсу — поля
   *  сохраняются на blur, и подсказка иначе гасла бы лишь после PUT и рефетча. */
  local: CourseWithoutBlocks
  /** «Перейти» у нарушения модерации: окно и страница настроек уводят к нему по-своему. */
  onNavigate: (target: ViolationGo) => void
}

export const CourseAccessBlock = ({ course, local, onNavigate }: Props) => {
  const m = useMessages(messages)
  const isCourseAuthor = useIsCourseAuthor(course)
  const courseAccess = useCourseAccess(course)
  const { validationErrors, clearValidationErrors } = courseAccess
  const [updateCourse] = useUpdateCourseMutation()
  const startedRun = useHasStartedTranslationRun(course.id)
  // Включённый второй язык тоже обязан иметь название и описание — иначе в
  // каталоге курс показался бы на нём пустым.
  const secondLang = translationLangOf(local)
  const unfilledHint = !isLangSlotComplete(seedLangFor(local), local)
    ? m.unfilledBase
    : hasTranslationRow(course, startedRun) && !isLangSlotComplete(secondLang, local)
      ? m.unfilledSecond(LANG_LABEL[secondLang])
      : undefined

  // Черновик, а не чтение прямо из course: categoryIds — полная замена на бэке, а
  // обновлённый курс приходит асинхронно, и быстрый второй клик иначе считал бы
  // следующий список от устаревшего и стирал бы первый выбор.
  const [categoryIds, setCategoryIds] = useState<number[]>(() =>
    (course.categories ?? []).map((c) => c.id),
  )
  // «Не знаем блоки урока» ≠ «пусто»: пока гейт не ответил, повышение открытости закрыто.
  const [videoGate, setVideoGate] = useState<EmptyVideoGateState>({
    isLoading: true,
    isError: false,
    empties: [],
    retry: () => {},
  })
  // Проверка пустых видео шлёт запрос за экранами КАЖДОГО урока разом. У большого
  // курса это десятки запросов, и разделы каталога и статус публикации вставали за
  // ними в очередь (а часть сервер отбивал 503). Запускаем её чуть позже — до её
  // ответа повышение доступа и так закрыто (`isLoading`).
  const [gateReady, setGateReady] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setGateReady(true), 1200)
    return () => clearTimeout(t)
  }, [])
  // Переполнение составного блока api не проверит, его меряет браузер (зонды панели ниже).
  const [overflow, setOverflow] = useState<{ pending: boolean; issues: BlockIssue[] }>({
    pending: true,
    issues: [],
  })
  const raiseChecking = videoGate.isLoading || videoGate.isError || overflow.pending
  const raiseRefusal =
    emptyVideoRefusal(videoGate.empties) ?? designOverflowRefusal(overflow.issues)

  // Оптимистичный черновик обязан откатываться, иначе после упавшего PUT пикер
  // показывал бы выбор, которого на сервере нет.
  const saveCategories = (next: number[]) => {
    const prev = categoryIds
    setCategoryIds(next)
    void updateCourse({ id: course.id, patch: { categoryIds: next } })
      .unwrap()
      .catch(() => setCategoryIds(prev))
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5">
        {gateReady ? (
          <EmptyVideoGate lessons={course.lessons ?? []} onChange={setVideoGate} />
        ) : null}
        <EmptyVideoStatus gate={videoGate} />
        <PublishDialogBody
          course={course}
          courseAccess={courseAccess}
          categoryIds={categoryIds}
          onCategoryIdsChange={saveCategories}
          raiseChecking={raiseChecking}
          raiseRefusal={raiseRefusal}
          isCourseAuthor={isCourseAuthor}
          onNavigate={() => {}}
          onGo={onNavigate}
          noteBeside
          unfilledHint={unfilledHint}
        />
        <PublishIssuesPanel
          course={course}
          local={local}
          ready={gateReady}
          onGo={onNavigate}
          onClientIssues={setOverflow}
        />
      </div>
      <ValidationErrorsDialog
        open={validationErrors.length > 0}
        errors={validationErrors}
        onClose={clearValidationErrors}
      />
    </TooltipProvider>
  )
}
